/**
 * Whether scrolling the message body should collapse or re-expand the
 * extracted-intelligence panel above it.
 *
 * Pure because the interesting part is a feedback loop that only shows up at
 * particular heights, and heights are easier to assert than to reproduce.
 */

export interface ScrollMetrics {
  scrollTop: number;
  /** Visible height of the body's scroll container. */
  clientHeight: number;
  /** Total height of the body's content. */
  scrollHeight: number;
  /** Height the panel occupies when expanded — what collapsing gives back. */
  extractionHeight: number;
}

/** Past this much scrolled, the reader is into the message. */
const INTO_MESSAGE = 80;
/** Within this much of the top, the reader is back at the header. */
const AT_TOP = 8;

export const nextExtractionState = (
  metrics: ScrollMetrics,
): "collapse" | "expand" | "keep" => {
  const { scrollTop, clientHeight, scrollHeight, extractionHeight } = metrics;

  if (scrollTop > INTO_MESSAGE) {
    // Collapsing hands the body `extractionHeight` more px, so it only pays
    // for itself when the message is taller than that. On a short one it
    // isn't: the body fits, the browser clamps scrollTop to 0, and the clamp
    // reads as "back at the top" — which re-expands, which makes it scrollable
    // again. The two states flip against each other while the wheel moves.
    const roomAfterCollapse = scrollHeight - clientHeight - extractionHeight;
    return roomAfterCollapse > INTO_MESSAGE ? "collapse" : "keep";
  }

  // Safe unconditionally in this direction: expanding only makes the body more
  // scrollable, so scrollTop stays where the reader put it.
  if (scrollTop < AT_TOP) return "expand";

  return "keep";
};
